import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { communityApi } from '../../lib/api';
import { REPORT_REASONS, REPORT_DETAILS_MAX, apiErrorMessage } from '../../lib/communityValidation';

// target: { type: 'question' | 'answer', id } or null (closed)
const ReportDialog = ({ target, onClose }) => {
  const [reason, setReason] = useState('spam');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) {
      setReason('spam');
      setDetails('');
    }
  }, [target]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await communityApi.report(target.type, target.id, reason, details.trim());
      toast.success('Thanks — a moderator will review this.');
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to submit report'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo uppercase">Report {target?.type}</DialogTitle>
          <DialogDescription>
            Reports go to the moderators. They're anonymous to the post's author.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="sr-only">Reason</legend>
            {REPORT_REASONS.map((r) => (
              <label key={r.value} htmlFor={`report-${r.value}`} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  id={`report-${r.value}`}
                  name="report-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="h-4 w-4 accent-autoxium-blue"
                />
                {r.label}
              </label>
            ))}
          </fieldset>
          <div className="space-y-2">
            <Label htmlFor="report-details">Details (optional)</Label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={REPORT_DETAILS_MAX}
              rows={3}
              placeholder="Anything that helps the moderator decide"
              data-testid="report-details-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-destructive hover:bg-destructive/90 text-white font-bold uppercase tracking-wider"
              data-testid="submit-report-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
